/**
 * Anomaly List (#579).
 *
 * Lists persisted anomaly events for the current tenant with severity /
 * resolution-state filters. Admins can mark a row resolved with a
 * short note. Tenant scoping is enforced server-side from the JWT.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  AnomalyRow,
  AnomalySeverity,
  activityService,
} from '@services/activity.service';
import { useTranslation } from 'react-i18next';

const SEVERITY_COLOR: Record<AnomalySeverity, 'default' | 'info' | 'warning' | 'error'> = {
  low: 'default',
  medium: 'info',
  high: 'warning',
  critical: 'error',
};

const formatDateTime = (iso: string | null): string => {
  if (!iso) return '-';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const AnomalyListPage: React.FC = () => {
  const { t } = useTranslation();
  const [items, setItems] = useState<AnomalyRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [severity, setSeverity] = useState<'' | AnomalySeverity>('');
  const [resolved, setResolved] = useState<'' | 'true' | 'false'>('false');
  const [dialogTarget, setDialogTarget] = useState<AnomalyRow | null>(null);
  const [dialogNote, setDialogNote] = useState('');
  const [resolving, setResolving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await activityService.listAnomalies({
        severity: severity || undefined,
        resolved: resolved === '' ? undefined : resolved === 'true',
        limit: 100,
      });
      setItems(data.items);
      setTotal(data.total);
    } catch (e) {
      setError(t('anomalies.fetchFailed', { message: (e as Error).message ?? e }));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- t only builds the fetch-error message; excluded so a language change doesn't refetch the anomaly list (PR #790)
  }, [severity, resolved]);

  useEffect(() => { load(); }, [load]);

  const onResolve = useCallback(async () => {
    if (!dialogTarget) return;
    setResolving(true);
    try {
      await activityService.resolveAnomaly(dialogTarget.id, dialogNote || undefined);
      setDialogTarget(null);
      setDialogNote('');
      await load();
    } catch (e) {
      setError(t('anomalies.resolveFailed', { message: (e as Error).message ?? e }));
    } finally {
      setResolving(false);
    }
  }, [dialogTarget, dialogNote, load, t]);

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>{t('anomalies.title')}</Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="body2">{t('anomalies.severity')}:</Typography>
          <Select
            size="small"
            value={severity}
            onChange={(e) => setSeverity(e.target.value as '' | AnomalySeverity)}
          >
            <MenuItem value="">{t('common.all')}</MenuItem>
            <MenuItem value="low">{t('anomalies.severityValues.low')}</MenuItem>
            <MenuItem value="medium">{t('anomalies.severityValues.medium')}</MenuItem>
            <MenuItem value="high">{t('anomalies.severityValues.high')}</MenuItem>
            <MenuItem value="critical">{t('anomalies.severityValues.critical')}</MenuItem>
          </Select>

          <Typography variant="body2">{t('anomalies.status')}:</Typography>
          <Select
            size="small"
            value={resolved}
            onChange={(e) => setResolved(e.target.value as '' | 'true' | 'false')}
          >
            <MenuItem value="false">{t('anomalies.unresolved')}</MenuItem>
            <MenuItem value="true">{t('anomalies.resolved')}</MenuItem>
            <MenuItem value="">{t('common.all')}</MenuItem>
          </Select>

          <Box flex={1} />
          <Typography variant="caption" color="text.secondary">
            {t('anomalies.totalCount', { total: total.toLocaleString() })}
          </Typography>
        </Stack>
      </Paper>

      {error && (
        <Paper sx={{ p: 2, mb: 2, bgcolor: 'error.light', color: 'error.contrastText' }}>
          {error}
        </Paper>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" my={4}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('audit.time')}</TableCell>
                <TableCell>{t('anomalies.type')}</TableCell>
                <TableCell>{t('anomalies.severity')}</TableCell>
                <TableCell>{t('anomalies.path')}</TableCell>
                <TableCell>HTTP</TableCell>
                <TableCell>IP</TableCell>
                <TableCell>{t('anomalies.status')}</TableCell>
                <TableCell>{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>{formatDateTime(row.created_at)}</TableCell>
                  <TableCell><code>{row.event_type}</code></TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={t(`anomalies.severityValues.${row.severity}`)}
                      color={SEVERITY_COLOR[row.severity]}
                    />
                  </TableCell>
                  <TableCell>
                    {row.request_method ? (
                      <Typography variant="caption">
                        {row.request_method} {row.request_path ?? ''}
                      </Typography>
                    ) : '-'}
                  </TableCell>
                  <TableCell>{row.response_status ?? '-'}</TableCell>
                  <TableCell>{row.ip_address ?? '-'}</TableCell>
                  <TableCell>
                    {row.resolved ? (
                      <Chip label={t('anomalies.resolved')} size="small" color="success" />
                    ) : (
                      <Chip label={t('anomalies.unresolved')} size="small" color="warning" />
                    )}
                  </TableCell>
                  <TableCell>
                    {row.resolved ? (
                      <Typography variant="caption" color="text.secondary">
                        {formatDateTime(row.resolved_at)}
                      </Typography>
                    ) : (
                      <Button size="small" onClick={() => setDialogTarget(row)}>
                        {t('anomalies.markResolved')}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="text.secondary">
                      {t('anomalies.noMatchingAnomalies')}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={dialogTarget !== null}
        onClose={() => setDialogTarget(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{t('anomalies.resolveDialogTitle')}</DialogTitle>
        <DialogContent>
          {dialogTarget && (
            <Stack spacing={1.5} mt={1}>
              <Typography variant="body2">
                <strong>{t('anomalies.type')}:</strong> {dialogTarget.event_type}
              </Typography>
              <Typography variant="body2">
                <strong>{t('anomalies.severity')}:</strong> {t(`anomalies.severityValues.${dialogTarget.severity}`)}
              </Typography>
              <Typography variant="body2">
                <strong>{t('anomalies.path')}:</strong>{' '}
                {dialogTarget.request_method} {dialogTarget.request_path ?? ''}
              </Typography>
              <TextField
                multiline
                rows={3}
                fullWidth
                label={t('anomalies.resolveNote')}
                value={dialogNote}
                onChange={(e) => setDialogNote(e.target.value)}
                inputProps={{ maxLength: 500 }}
                helperText={`${dialogNote.length}/500`}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogTarget(null)} disabled={resolving}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={onResolve}
            disabled={resolving}
          >
            {t('anomalies.markResolved')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AnomalyListPage;
